import React, {
  ElementRef,
  FC,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
} from 'react';

import { FlatList, View } from 'react-native';

import { isWeb } from 'utils/environment';
import noop from 'utils/noop';

export type AnchorScrollContainerRefType = ElementRef<typeof FlatList>;
export type AnchorComponentRefType = View;

/**
 * Resolves the underlying native host component ref from a FlatList.
 */
const getNativeScrollRef = (container: AnchorScrollContainerRefType): any => {
  if (typeof container?.getNativeScrollRef === 'function') {
    return container.getNativeScrollRef();
  }
  return null;
};

interface AnchorContextType {
  registerContainerRef: (container: AnchorScrollContainerRefType) => void;
  registerTarget: (name: string, ref: AnchorComponentRefType) => void;
  unregisterTarget: (name: string) => void;
  scrollToTarget: (name: string, animated?: boolean) => void;
  formatLocaleAnchorName: (anchorName: string) => string;
}

export const AnchorContext = createContext<AnchorContextType>(null!);

export const AnchorContextProvider: FC<React.PropsWithChildren<unknown>> = ({ children }) => {
  const scrollContainer = useRef<AnchorScrollContainerRefType>(null);
  const targets = useRef<Record<string, AnchorComponentRefType>>({});
  const registerContainerRef = (container: AnchorScrollContainerRefType) => {
    scrollContainer.current = container;
  };

  const registerTarget = useCallback((anchorName: string, ref: AnchorComponentRefType) => {
    targets.current[anchorName] = ref;
  }, []);

  const unregisterTarget = useCallback((anchorName: string) => {
    delete targets.current[anchorName];
  }, []);

  const formatLocaleAnchorName = useCallback(
    (anchorName: string) => anchorName.replace(/\s/g, ''),
    []
  );

  const scrollTo = (target: AnchorComponentRefType, animated: boolean = false) => {
    // DO NOT REMOVE isWeb check. it isn't supported on react-native-web

    if (scrollContainer.current) {
      // On web, we can use the DOM directly
      if (isWeb) {
        const containerElement = scrollContainer.current as any;
        const targetElement = target as any;
        if (containerElement && targetElement) {
          targetElement.scrollIntoView({
            behavior: animated ? 'smooth' : 'auto',
            block: 'start',
          });
        }
      } else {
        const nativeRef = getNativeScrollRef(scrollContainer.current);
        if (nativeRef) {
          target.measureLayout(
            nativeRef,
            (_, y) => {
              scrollContainer.current!.scrollToOffset({ offset: y, animated });
            },
            noop // fail silently
          );
        }
      }
    }
  };

  const timerId = React.useRef<ReturnType<typeof requestAnimationFrame>>(undefined);
  const scrollToOffscreenElement = async (anchorName: string, animated: boolean) => {
    const waitForAnchor = async (): Promise<AnchorComponentRefType | void> => {
      if (timerId.current) {
        cancelAnimationFrame(timerId.current);
      }
      const element = Object.entries(targets.current).find(([key]) => key === anchorName);
      if (element) {
        // element is now mounted
        scrollTo(element[1], animated);
        return element[1];
      }
      scrollContainer.current?.scrollToEnd({ animated });
      timerId.current = requestAnimationFrame(waitForAnchor);
      // @TODO case when the anchor is never found causing an infinite loop.
      // The timer will still be cleared on unMount but undefined behaviour or performance degradation
      // may arise in this case
    };
    return waitForAnchor();
  };
  useEffect(() => {
    // cleanup scroll anchor timer incase anchor is never found or user navigates causing an unmount
    return () => {
      if (timerId.current) {
        cancelAnimationFrame(timerId.current);
      }
    };
  }, []);

  const scrollToTarget = async (anchorName: string, animated: boolean = false) => {
    const formattedAnchorName = formatLocaleAnchorName(anchorName);
    const target = targets.current[formattedAnchorName];
    if (!target) {
      // no target found in view, scroll down more to find it so those anchor refs will be mounted
      await scrollToOffscreenElement(formattedAnchorName, animated);
      return;
    }
    scrollTo(target, animated);
  };

  return (
    <AnchorContext.Provider
      value={{
        registerContainerRef,
        registerTarget,
        unregisterTarget,
        scrollToTarget,
        formatLocaleAnchorName,
      }}
    >
      {children}
    </AnchorContext.Provider>
  );
};

export const useAnchorContext = () => useContext(AnchorContext);
